import type { AppState, Client } from '../types'
import { CLIENT_STATUS_OPTIONS } from '../types'

const MAX_BACKUP_BYTES = 20 * 1024 * 1024
const MAX_BACKUP_CLIENTS = 10_000
const MAX_INTERACTIONS = 500
const MAX_SHORT_TEXT = 2_000
const MAX_LONG_TEXT = 50_000
const VALID_STATUSES = new Set(CLIENT_STATUS_OPTIONS.map(({ value }) => value))

function download(content: BlobPart, type: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10)
}

export function csvCell(value: unknown): string {
  const rawValue = String(value ?? '')
  const stringValue = /^[\s]*[=+\-@]/.test(rawValue) ? `'${rawValue}` : rawValue
  return `"${stringValue.replaceAll('"', '""')}"`
}

function isText(value: unknown, maxLength = MAX_SHORT_TEXT): value is string {
  return typeof value === 'string' && value.length <= maxLength
}

function isNullableDate(value: unknown): value is string | null {
  return value === null || (
    isText(value) &&
    Number.isFinite(Date.parse(value))
  )
}

function isInteraction(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const interaction = value as Record<string, unknown>
  return (
    isText(interaction.id, 128) &&
    isText(interaction.createdAt) &&
    Number.isFinite(Date.parse(interaction.createdAt)) &&
    typeof interaction.status === 'string' &&
    VALID_STATUSES.has(interaction.status as Client['status']) &&
    isText(interaction.outcome, MAX_LONG_TEXT) &&
    isNullableDate(interaction.nextContactAt)
  )
}

function isNonNegativeNumberOrNull(value: unknown): boolean {
  return (
    value === null ||
    (typeof value === 'number' && Number.isFinite(value) && value >= 0)
  )
}

function isOsintContract(value: unknown): boolean {
  if (value === null) return true
  if (!value || typeof value !== 'object') return false
  const contract = value as Record<string, unknown>
  return (
    isText(contract.laws) &&
    (contract.date === null || isText(contract.date)) &&
    isNonNegativeNumberOrNull(contract.price) &&
    isText(contract.customer, MAX_LONG_TEXT) &&
    isText(contract.subject, MAX_LONG_TEXT) &&
    isText(contract.registryNumber) &&
    isText(contract.url)
  )
}

function isTenderProfile(value: unknown): boolean {
  if (value === null) return true
  if (!value || typeof value !== 'object') return false
  const profile = value as Record<string, unknown>
  return (
    isText(profile.matchedName) &&
    typeof profile.exactMatch === 'boolean' &&
    Number.isInteger(profile.winners) &&
    (profile.winners as number) >= 0 &&
    Number.isInteger(profile.completed) &&
    (profile.completed as number) >= 0 &&
    Number.isInteger(profile.total) &&
    (profile.total as number) >= 0 &&
    isText(profile.organizationUrl) &&
    isText(profile.searchUrl)
  )
}

function isOsint(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const osint = value as Record<string, unknown>
  const contact =
    osint.contact && typeof osint.contact === 'object'
      ? (osint.contact as Record<string, unknown>)
      : null
  const sources = osint.sources
  return (
    isText(osint.collectedAt) &&
    isText(osint.sourceFile) &&
    isNonNegativeNumberOrNull(osint.rank) &&
    (osint.legalType === '' || osint.legalType === 'ЮЛ' || osint.legalType === 'ИП') &&
    isNonNegativeNumberOrNull(osint.revenue) &&
    isNonNegativeNumberOrNull(osint.revenueYear) &&
    isText(osint.revenueCategory) &&
    isText(osint.segment) &&
    isText(osint.companyStatus) &&
    ['yes', 'not_found', 'unknown'].includes(String(osint.governmentContracts)) &&
    typeof osint.minimumContracts === 'number' &&
    Number.isInteger(osint.minimumContracts) &&
    (osint.minimumContracts as number) >= 0 &&
    isText(osint.activity, MAX_LONG_TEXT) &&
    isText(osint.completeness) &&
    isText(osint.classDemand) &&
    isText(osint.profileClass) &&
    isText(osint.kpp) &&
    contact !== null &&
    isText(contact.address, MAX_LONG_TEXT) &&
    isText(contact.phone) &&
    isText(contact.email) &&
    isText(contact.website) &&
    isText(contact.source) &&
    isText(contact.comment, MAX_LONG_TEXT) &&
    isOsintContract(osint.latestContract) &&
    isTenderProfile(osint.tenderProfile) &&
    Array.isArray(sources) &&
    sources.length <= 50 &&
    sources.every((source) => isText(source))
  )
}

function isClient(value: unknown): value is Client {
  if (!value || typeof value !== 'object') return false
  const client = value as Record<string, unknown>
  return (
    isText(client.id, 128) &&
    !client.id.includes('/') &&
    isText(client.company) &&
    (client.taxId === undefined || isText(client.taxId, 32)) &&
    isText(client.contactPerson) &&
    isText(client.phone) &&
    isText(client.email) &&
    isText(client.city) &&
    isText(client.segment) &&
    (client.potential === null ||
      (typeof client.potential === 'number' &&
        Number.isFinite(client.potential) &&
        client.potential >= 0)) &&
    (client.paymentTermsDays === undefined || (
      Number.isInteger(client.paymentTermsDays) &&
      (client.paymentTermsDays as number) >= 0 &&
      (client.paymentTermsDays as number) <= 365
    )) &&
    typeof client.status === 'string' &&
    VALID_STATUSES.has(client.status as Client['status']) &&
    typeof client.important === 'boolean' &&
    Number.isInteger(client.attempts) &&
    (client.attempts as number) >= 0 &&
    isNullableDate(client.lastContactAt) &&
    isNullableDate(client.nextContactAt) &&
    isText(client.result, MAX_LONG_TEXT) &&
    isText(client.notes, MAX_LONG_TEXT) &&
    Array.isArray(client.interactions) &&
    client.interactions.length <= MAX_INTERACTIONS &&
    client.interactions.every(isInteraction) &&
    (client.osint === undefined || isOsint(client.osint)) &&
    isText(client.createdAt) &&
    Number.isFinite(Date.parse(client.createdAt)) &&
    isText(client.updatedAt) &&
    Number.isFinite(Date.parse(client.updatedAt))
  )
}

export function exportBackup(state: AppState): void {
  download(
    JSON.stringify(state, null, 2),
    'application/json;charset=utf-8',
    `worksmarter-backup-${dateStamp()}.json`,
  )
}

export function exportCsv(clients: Client[]): void {
  const statusLabels = new Map(
    CLIENT_STATUS_OPTIONS.map((status) => [status.value, status.label]),
  )
  const rows = [
    [
      'Компания',
      'ИНН',
      'Контактное лицо',
      'Телефон',
      'E-mail',
      'Город',
      'Сегмент',
      'Потенциал',
      'Статус',
      'Результат',
      'Следующий контакт',
      'Попытки',
      'Важный',
      'Комментарий',
      'Рейтинг OSINT',
      'Сегмент OSINT',
      'Тип',
      'Категория выручки',
      'Выручка',
      'Год выручки',
      'Статус компании',
      'Госконтракты',
      'Мин. контрактов',
      'Деятельность',
      'Источники OSINT',
    ],
    ...clients.map((client) => [
      client.company,
      client.taxId ?? '',
      client.contactPerson,
      client.phone,
      client.email,
      client.city,
      client.segment,
      client.potential ?? '',
      statusLabels.get(client.status) ?? client.status,
      client.result,
      client.nextContactAt ?? '',
      client.attempts,
      client.important ? 'Да' : 'Нет',
      client.notes,
      client.osint?.rank ?? '',
      client.osint?.segment ?? '',
      client.osint?.legalType ?? '',
      client.osint?.revenueCategory ?? '',
      client.osint?.revenue ?? '',
      client.osint?.revenueYear ?? '',
      client.osint?.companyStatus ?? '',
      client.osint?.governmentContracts === 'yes'
        ? 'Да'
        : client.osint?.governmentContracts === 'not_found'
          ? 'Не найдено'
          : '',
      client.osint?.minimumContracts ?? '',
      client.osint?.activity ?? '',
      client.osint?.sources.join('\n') ?? '',
    ]),
  ]
  const csv = `\uFEFF${rows.map((row) => row.map(csvCell).join(';')).join('\r\n')}`
  download(csv, 'text/csv;charset=utf-8', `worksmarter-clients-${dateStamp()}.csv`)
}

export async function readBackup(file: File): Promise<AppState> {
  if (file.size > MAX_BACKUP_BYTES) {
    throw new Error('Резервная копия слишком большая. Максимальный размер — 20 МБ.')
  }
  const parsed = JSON.parse(await file.text()) as Partial<AppState>
  if (
    parsed.schemaVersion !== 1 ||
    !Array.isArray(parsed.clients) ||
    parsed.clients.length > MAX_BACKUP_CLIENTS ||
    !parsed.clients.every(isClient)
  ) {
    throw new Error('Это не резервная копия WorkSmarter.')
  }
  return {
    schemaVersion: 1,
    clients: parsed.clients,
    lastSavedAt: new Date().toISOString(),
  }
}
